/**
 * The two Xero **Sales defaults** controls, wherever they are set.
 *
 * Xero's Sales defaults for a customer: what Xero should assume when it
 * invoices them. Set in two places today — on the company record, where they
 * belong, and in the pre-push step on a quote, where a missing one is what
 * stops the push — and the two must never disagree about wording, about which
 * rates are offered, or about what a blank means.
 *
 * ⚠️ **The wording of the line-amount field is correctness, not style.** It
 * says what Xero should default to. It must never say how anything is
 * calculated, because EMERGE's own arithmetic is tax-exclusive with no
 * inclusive mode at all — a company can carry `INCLUSIVE` here while every
 * EMERGE figure is still exclusive. A label implying otherwise would leave the
 * two systems printing different totals for one sale with nothing flagging it.
 *
 * `null` is offered as **"Use organisation settings"** on both, because that is
 * the first option in Xero's own dropdowns and a real answer rather than a
 * missing one. Neither field is ever defaulted to anything.
 *
 * The data and the vocabulary live in `@/lib/xero-tax-rates`; this file is the
 * rendering alone, which is what lets the two callers lay the controls out
 * differently without either forking the logic to do it.
 */

import { useMemo, type ReactNode } from 'react';
import type { XeroTaxRate } from '@/lib/types';
import {
  LINE_AMOUNT_OPTIONS,
  USE_ORGANISATION_SETTINGS,
  sellableTaxRates,
  taxRateHint,
  type TaxRatesFailure,
} from '@/lib/xero-tax-rates';
import { cx } from '@/lib/cx';
import { joinList } from '@/lib/format';
import { Button, Combobox, Field, Select } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';

export type XeroSalesDefaultsKey = 'xeroSalesLineAmountType' | 'xeroSalesTaxType';

export interface XeroSalesDefaultsFieldsProps {
  lineAmountType: string;
  taxType: string;
  errors: Record<string, string>;
  rates: XeroTaxRate[];
  ratesLoading: boolean;
  ratesError: TaxRatesFailure | null;
  onReloadRates: () => void;
  connectionLoading: boolean;
  connected: boolean;
  /**
   * True when the API has no `/xero` routes at all yet — a calm state, not a fault.
   *
   * ⚠️ **Accepted and deliberately UNREAD since 2026-09-05.** Its two sentences
   * were the only thing that ever consulted it, and both were absence reports
   * that the owner's helper-text ruling removed. The prop stays declared so the
   * two callers keep compiling and so re-rendering an "unavailable" state here
   * needs no contract change — it is not an oversight, and it must not be
   * "cleaned up" into a second signature for the same component.
   */
  notInstalled: boolean;
  /**
   * The code already stored on the record, and the cached name for it.
   *
   * Injected into the options when the live list does not carry it — an
   * accountant can retire or rename a rate after the company was filed. Left
   * out, the control renders blank, which reads as "use organisation settings"
   * and is one stray save away from silently clearing a real default. The name
   * is the only label we have for a code Xero no longer lists.
   */
  storedTaxType: string | null;
  storedTaxName: string | null;
  /** Which defaults the user has moved back to "use organisation settings". */
  cleared?: readonly string[];
  /**
   * `stacked` for a narrow container such as a dialog. The controls are
   * otherwise a two-column grid, which is what the company form's section
   * wants and what a dialog cannot give them at 320px.
   */
  layout?: 'grid' | 'stacked';
  onChange: (key: XeroSalesDefaultsKey, value: string) => void;
}

export function XeroSalesDefaultsFields({
  lineAmountType,
  taxType,
  errors,
  rates,
  ratesLoading,
  ratesError,
  onReloadRates,
  connectionLoading,
  connected,
  storedTaxType,
  storedTaxName,
  cleared,
  layout = 'grid',
  onChange,
}: XeroSalesDefaultsFieldsProps) {
  const options = useMemo<ComboboxOption[]>(() => {
    /*
     * REVENUE RATES ONLY — an expense code on a sales document is refused at
     * push time (`422 XERO_TAX_TYPE_NOT_REVENUE`), so offering one here is a
     * choice that can only fail later. Not theoretical: `RRINPUT` ("VAT on
     * Expenses") and `RROUTPUT` ("VAT on Income") are three characters apart,
     * both 5%, both active — a hint on the option was not enough to stop the
     * mix-up. `sellableTaxRates` still keeps a stored expense-rate code
     * selectable, so a company already filed against a bad code renders it
     * rather than going blank and being cleared by the next save.
     */
    const list = sellableTaxRates(rates, storedTaxType ?? '').map((rate) => ({
      value: rate.taxType,
      label: rate.name,
      hint: taxRateHint(rate),
    }));
    /*
     * The other injection, and a different case: a code Xero no longer lists
     * AT ALL. `sellableTaxRates` can only re-add what is in `rates`, so this
     * still tests the full list and the two can never both fire.
     */
    if (
      storedTaxType &&
      !rates.some((rate) => rate.taxType.toUpperCase() === storedTaxType.toUpperCase())
    ) {
      list.unshift({
        value: storedTaxType,
        label: storedTaxName ?? storedTaxType,
        hint: rates.length > 0 ? `${storedTaxType} · not in the current Xero list` : storedTaxType,
      });
    }
    return list;
  }, [rates, storedTaxType, storedTaxName]);

  const selectedRate = rates.find(
    (rate) => taxType !== '' && rate.taxType.toUpperCase() === taxType.toUpperCase(),
  );
  const ratesUnavailable = connectionLoading || !connected || ratesError !== null;

  /*
   * Rates unreachable does NOT mean the field is frozen — CLEARING still works.
   *
   * Verified in the backend service rather than inferred: `resolveTaxDefaults`
   * returns early on `null` (`if (xeroSalesTaxType === null) return
   * { xeroSalesTaxName: null }`) and never calls Xero. Only a non-null code is
   * resolved against the live list, which is the call that can 422 or 502.
   *
   * So locking the control whenever Xero is unreachable would trap a company on
   * a tax default it can no longer be released from — for a request the server
   * would accept without contacting Xero at all. The picker stays usable when
   * there is something to clear; it is only inert when it is already empty and
   * there is nothing to choose from.
   */
  const canOnlyClear = ratesUnavailable && taxType !== '';

  /*
   * ⚠️ **Cut to REFUSALS ONLY — owner's helper-text ruling, 2026-09-05.** Every
   * loading and absence branch is gone: *"Checking the Xero connection…"*,
   * *"Xero is not connected/not set up … so its rates cannot be listed"*,
   * *"Reading this organisation's rates from Xero…"*, the generic
   * *"The rate list could not be read"*, and *"Xero will fall back to the rate
   * set on the organisation"* — the last of which was the empty option's own
   * label (`USE_ORGANISATION_SETTINGS`) said a second time.
   *
   * The two that stay are the two that name a REFUSAL:
   *
   * - the COOLDOWN, which is Xero's rate limit refusing the read. It says its
   *   own sentence rather than borrowing a generic one, and says it SHORT:
   *   "could not be read" invites a retry, and a retry is the one thing that
   *   cannot work while the budget is spent.
   * - what the CHOSEN CODE means for the push. `taxRateHint` carries the
   *   three-state classification that stops an expense rate being filed where a
   *   revenue one was meant (`422 XERO_TAX_TYPE_NOT_REVENUE`), and the last
   *   branch names a stored code Xero no longer lists, which is
   *   `422 XERO_TAX_TYPE_UNKNOWN` waiting to happen at push time.
   *
   * ⚠️ A failed read is NOT silent as a result — the alert above this field
   * renders the server's own `ratesError.message` with a Try again button. That
   * is what makes removing the generic branch a de-duplication rather than a
   * loss.
   */
  const taxHint: ReactNode =
    connectionLoading || !connected
      ? undefined
      : ratesError
        ? ratesError.kind === 'cooldown'
          ? taxType === ''
            ? 'Xero’s rate limit has been reached, so no rate can be chosen right now.'
            : 'Xero’s rate limit has been reached. The rate already chosen can still be cleared.'
          : undefined
        : ratesLoading || taxType === ''
          ? undefined
          : selectedRate
            ? taxRateHint(selectedRate)
            : `Sent as ${taxType}. Xero does not currently list that code.`;

  return (
    <div className="space-y-3">
      {connected && ratesError && (
        <Notice
          role="alert"
          className="break-words"
          /*
           * A cooldown is a calm state of a HEALTHY connection, so it takes
           * the warning band rather than the danger one. Red here would say
           * something is wrong, and nothing is.
           */
          tone={
            ratesError.kind === 'retry' ||
            ratesError.kind === 'missing' ||
            ratesError.kind === 'cooldown'
              ? 'warning'
              : 'danger'
          }
          action={
            ratesError.kind !== 'missing' ? (
              <Button variant="secondary" size="sm" onClick={onReloadRates}>
                Try again
              </Button>
            ) : undefined
          }
        >
          {ratesError.message}{' '}
          {ratesError.kind === 'reconnect' && (
            <TextLink to="/integrations" className="font-medium underline">
              Re-authorise Xero
            </TextLink>
          )}
        </Notice>
      )}

      <div className={cx('grid gap-3', layout === 'grid' && 'sm:grid-cols-2')}>
        <Field
          label="Line amounts Xero should default to"
          error={errors.xeroSalesLineAmountType}
        >
          {({ id, 'aria-describedby': describedBy }) => (
            <Select
              id={id}
              value={lineAmountType}
              invalid={Boolean(errors.xeroSalesLineAmountType)}
              aria-describedby={describedBy}
              onChange={(event) => onChange('xeroSalesLineAmountType', event.target.value)}
            >
              <option value="">{USE_ORGANISATION_SETTINGS}</option>
              {LINE_AMOUNT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Sales tax rate Xero should default to" error={errors.xeroSalesTaxType} hint={taxHint}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Combobox
              id={id}
              value={taxType}
              options={options}
              placeholder={ratesLoading ? 'Reading rates from Xero…' : USE_ORGANISATION_SETTINGS}
              emptyOptionLabel={USE_ORGANISATION_SETTINGS}
              searchPlaceholder="Search by name or code…"
              loading={ratesLoading}
              disabled={ratesUnavailable && !canOnlyClear}
              invalid={Boolean(errors.xeroSalesTaxType)}
              aria-describedby={describedBy}
              onChange={(next) => onChange('xeroSalesTaxType', next)}
            />
          )}
        </Field>
      </div>

      {/*
       * The clear-vs-set gap, stated plainly rather than left for someone to
       * discover from a Xero invoice. EMERGE can SET a default and cannot
       * CLEAR one: `null` stops us sending a value, and Xero has no way to
       * express "unset", so the override already on the contact stays.
       */}
      {cleared && cleared.length > 0 && (
        <Notice tone="warning" role="status" className="break-words">
          Saving clears {joinList(cleared)} in EMERGE only. To remove it from the Xero contact,
          change it in Xero.
        </Notice>
      )}
    </div>
  );
}
